import React, { useState, useCallback, useEffect, useRef } from 'react';
import './App.css';

interface NodeInfo {
  nodeId: string;
  nodeIp: string;
  version: string;
  status: 'pending' | 'scanning' | 'success' | 'error';
  connectedNodes?: Record<string, [string, boolean]>;
  error?: string;
  scannedAt?: number;
  responseTime?: number;
}

interface ScanQueue {
  ip: string;
  port: number;
  isInitial?: boolean;
}

const App: React.FC = () => {
  const [nodes, setNodes] = useState<Map<string, NodeInfo>>(new Map());
  const [scanQueue, setScanQueue] = useState<ScanQueue[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    success: 0,
    error: 0,
    scanning: 0
  });

  const nodesRef = useRef(nodes);
  const scanQueueRef = useRef(scanQueue);
  const abortControllerRef = useRef<AbortController | null>(null);
  const activeScanCountRef = useRef(0);
  const processQueueIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  useEffect(() => {
    scanQueueRef.current = scanQueue;
  }, [scanQueue]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (processQueueIntervalRef.current) {
        clearInterval(processQueueIntervalRef.current);
      }
    };
  }, []);

  const get_rpc_url = async (node_ip: string) => {
    const rpcUrl = `https://proxy-rpc.massa.ga/${node_ip}`;

    try {
      await navigator.clipboard.writeText(rpcUrl);

      const button = document.activeElement as HTMLButtonElement;
      const originalText = button?.textContent;

      if (button) {
        button.textContent = '✓ Copied!';
        button.style.background = '#28a745';

        setTimeout(() => {
          button.textContent = originalText;
          button.style.background = '';
        }, 2000);
      }

      console.log(`RPC URL copied: ${rpcUrl}`);
    } catch (error) {
      console.error('Failed to copy:', error);

      const textArea = document.createElement('textarea');
      textArea.value = rpcUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();

      try {
        document.execCommand('copy');
        alert(`RPC URL copied: ${rpcUrl}`);
      } catch (err) {
        alert(`Please copy manually: ${rpcUrl}`);
      }

      document.body.removeChild(textArea);
    }
  };

  const cleanIp = useCallback((ip: string): string => {
    return ip.replace(/^::ffff:/i, '');
  }, []);

  const scanNode = useCallback(async (ip: string, port: number, isInitial: boolean = false): Promise<NodeInfo | null> => {
    const nodeKey = `${ip}:${port}`;
    const startTime = performance.now();

    activeScanCountRef.current++;

    setNodes(prev => {
      const newMap = new Map(prev);
      newMap.set(nodeKey, {
        nodeId: '',
        nodeIp: ip,
        version: '',
        status: 'scanning'
      });
      return newMap;
    });

    // Créer un AbortController local avec timeout
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), 2000); // 👈 Réduit à 2s

    try {
      const url = isInitial 
        ? 'https://mainnet.massa.net/api/v2'
        : `https://proxy-rpc.massa.ga/${ip}`;

      // Vérifier que le scan global n'est pas annulé
      const controller = abortControllerRef.current;
      if (!controller || controller.signal.aborted) {
        throw new Error('Scan cancelled');
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 1,
          method: 'get_status',
          params: []
        }),
        signal: timeoutController.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const endTime = performance.now();
      const responseTime = Math.round(endTime - startTime);

      if (data.error) {
        throw new Error(data.error.message || 'RPC Error');
      }

      const result = data.result;

      // Vérifier à nouveau avant de mettre à jour
      if (controller.signal.aborted) {
        throw new Error('Scan cancelled');
      }

      const nodeInfo: NodeInfo = {
        nodeId: result.node_id,
        nodeIp: result.node_ip || ip,
        version: result.version,
        status: 'success',
        connectedNodes: result.connected_nodes,
        scannedAt: Date.now(),
        responseTime: responseTime
      };

      setNodes(prev => {
        const newMap = new Map(prev);
        newMap.set(nodeKey, nodeInfo);
        return newMap;
      });

      // Ajouter les nouveaux nœuds à scanner
      if (result.connected_nodes) {
        const newNodesToScan: ScanQueue[] = [];

        Object.entries(result.connected_nodes as Record<string, [string, boolean]>).forEach(([, nodeData]) => {
          const [nodeIp] = nodeData;
          const cleanedIp = cleanIp(nodeIp);
          const newNodeKey = `${cleanedIp}:33035`;

          if (!nodesRef.current.has(newNodeKey) && 
              !scanQueueRef.current.some(q => `${q.ip}:${q.port}` === newNodeKey)) {
            newNodesToScan.push({
              ip: cleanedIp,
              port: 33035
            });
          }
        });

        if (newNodesToScan.length > 0) {
          setScanQueue(prev => [...prev, ...newNodesToScan]);
        }
      }

      return nodeInfo;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === 'AbortError') {
        const errorMessage = 'Timeout (2s)';
        
        setNodes(prev => {
          const newMap = new Map(prev);
          newMap.set(nodeKey, {
            nodeId: '',
            nodeIp: ip,
            version: '',
            status: 'error',
            error: errorMessage,
            scannedAt: Date.now(),
            responseTime: 2000
          });
          return newMap;
        });

        return null;
      }

      const endTime = performance.now();
      const responseTime = Math.round(endTime - startTime);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      setNodes(prev => {
        const newMap = new Map(prev);
        newMap.set(nodeKey, {
          nodeId: '',
          nodeIp: ip,
          version: '',
          status: 'error',
          error: errorMessage,
          scannedAt: Date.now(),
          responseTime: responseTime
        });
        return newMap;
      });

      return null;
    } finally {
      activeScanCountRef.current--;
      
      // Vérifier si le scan est terminé
      if (activeScanCountRef.current === 0 && scanQueueRef.current.length === 0) {
        setTimeout(() => {
          if (activeScanCountRef.current === 0 && scanQueueRef.current.length === 0) {
            setIsScanning(false);
            if (processQueueIntervalRef.current) {
              clearInterval(processQueueIntervalRef.current);
              processQueueIntervalRef.current = null;
            }
          }
        }, 500);
      }
    }
  }, [cleanIp]);

  // 👇 NOUVEAU: Processeur de queue avec contrôle de concurrence
  const processQueue = useCallback(async () => {
    const MAX_CONCURRENT = 5; // 👈 RÉDUIT à 5 requêtes simultanées max

    if (!isScanning || activeScanCountRef.current >= MAX_CONCURRENT) {
      return;
    }

    const queue = scanQueueRef.current;
    if (queue.length === 0) {
      return;
    }

    const batchSize = Math.min(MAX_CONCURRENT - activeScanCountRef.current, queue.length);
    const batch = queue.slice(0, batchSize);
    
    setScanQueue(prev => prev.slice(batchSize));

    // Lancer les scans en parallèle (limité à batchSize)
    await Promise.allSettled(
      batch.map(({ ip, port, isInitial }) => scanNode(ip, port, isInitial))
    );
  }, [isScanning, scanNode]);

  // 👇 Effet pour démarrer le processeur de queue
  useEffect(() => {
    if (isScanning && !processQueueIntervalRef.current) {
      processQueueIntervalRef.current = setInterval(() => {
        processQueue();
      }, 100); // 👈 Vérifier toutes les 100ms
    }

    if (!isScanning && processQueueIntervalRef.current) {
      clearInterval(processQueueIntervalRef.current);
      processQueueIntervalRef.current = null;
    }

    return () => {
      if (processQueueIntervalRef.current) {
        clearInterval(processQueueIntervalRef.current);
        processQueueIntervalRef.current = null;
      }
    };
  }, [isScanning, processQueue]);

  const startScan = useCallback(async () => {
    // Arrêter le scan précédent s'il existe
    if (isScanning) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (processQueueIntervalRef.current) {
        clearInterval(processQueueIntervalRef.current);
        processQueueIntervalRef.current = null;
      }
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    // Créer un nouveau controller
    abortControllerRef.current = new AbortController();
    activeScanCountRef.current = 0;

    setIsScanning(true);
    setNodes(new Map());
    setScanQueue([]);
    
    console.log('🚀 Starting new scan...');
    
    // Scanner le nœud initial
    await scanNode('mainnet.massa.net', 33035, true);
  }, [scanNode, isScanning]);

  const stopScan = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (processQueueIntervalRef.current) {
      clearInterval(processQueueIntervalRef.current);
      processQueueIntervalRef.current = null;
    }

    setIsScanning(false);
    setScanQueue([]);
    activeScanCountRef.current = 0;
    
    setNodes(prev => {
      const newMap = new Map(prev);
      newMap.forEach((node, key) => {
        if (node.status === 'scanning') {
          newMap.set(key, {
            ...node,
            status: 'error',
            error: 'Scan stopped by user'
          });
        }
      });
      return newMap;
    });
  }, []);

  // Calculer les stats
  useEffect(() => {
    const total = nodes.size;
    let success = 0;
    let error = 0;
    let scanning = 0;

    nodes.forEach(node => {
      if (node.status === 'success') success++;
      else if (node.status === 'error') error++;
      else if (node.status === 'scanning') scanning++;
    });

    setStats({ total, success, error, scanning });
  }, [nodes]);

  return (
    <div className="App">
      <header className="header">
        <h1>Massa Network Scanner</h1>
        <p>Scan and discover Massa nodes recursively</p>
      </header>

      <div className="controls">
        <div style={{ display: 'flex', gap: '10px' }}>
          {!isScanning ? (
            <button 
              onClick={startScan}
              className="btn-primary"
            >
              🚀 Start Scan
            </button>
          
          ) : (
            <button 
              onClick={stopScan}
              className="btn-danger"
            >
              ⏹️ Stop Scan
            </button>
          )}
        </div>

        <div className="stats">
          <div className="stat-item">
            <span className="stat-label">Total:</span>
            <span className="stat-value">{stats.total}</span>
          </div>
          <div className="stat-item success">
            <span className="stat-label">Success:</span>
            <span className="stat-value">{stats.success}</span>
          </div>
          <div className="stat-item error">
            <span className="stat-label">Error:</span>
            <span className="stat-value">{stats.error}</span>
          </div>
          <div className="stat-item scanning">
            <span className="stat-label">Scanning:</span>
            <span className="stat-value">{stats.scanning}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Queue:</span>
            <span className="stat-value">{scanQueue.length}</span>
          </div>
        </div>
      </div>

      <div className="nodes-list">
        {nodes.size !== 0 && (
          <div className="list-header">
            <div className="col-status">Status</div>
            <div className="col-id">ID / IP Address</div>
            <div className="col-version">Version</div>
            <div className="col-connections">Connections</div>
            <div className="col-action">Action</div>
          </div>
        )}

        {Array.from(nodes.values())
          .filter(node => node.status === 'success')
          .sort((a, b) => (a.responseTime || 9999) - (b.responseTime || 9999))
          .map((node, index) => (
            <div key={`${node.nodeIp}-${index}`} className="list-row">
              <div className="col-status">
                <span className="status-badge success"></span>
              </div>
              <div className="col-id">
                <div><b>{node.nodeId}</b></div>
                <div>IP: {node.nodeIp}</div>
              </div>
              <div className="col-version">{node.version}</div>
              <div className="col-connections">
                {node.connectedNodes ? Object.keys(node.connectedNodes).length : 0}
              </div>
              <div className="col-action">
                {!node.nodeIp.includes(":") ? (
                  <button 
                    className="btn-small"
                    onClick={() => get_rpc_url(node.nodeIp)}
                  >
                    Copy RPC URL
                  </button>
                ) : ('Not supported')}
              </div>
            </div>
          ))}
      </div>

      {nodes.size === 0 && !isScanning && (
        <div className="empty-state">
          <p>👆 Click "Start Scan" to begin discovering Massa nodes</p>
        </div>
      )}
      <div className="footer">Powered by <a href='https://massa.ga/'>massa.ga</a></div>
    </div>
  );
};

export default App;
